import { useCallback, useMemo } from 'react';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { useEventItem, useMarketItems } from 'modules/common/hooks';
import SelectionButton from 'modules/selection-button';
import SelectionButtonTypes from 'modules/selection-button/types/index.js';
import EventCardBadge from 'modules/event-cards/badge/index.js';

const { uiStyle } = MojitoCore.Presentation;
const { utils: EventUtils } = MojitoServices.SportsContent.Events;
const { Button, FlexPane, Image, Text, ParticipantIconsInline } = MojitoPresentation.Components;
const { MarketHelper } = MojitoPresentation.Utils;
const { getSeparatorTranslationKey } = MojitoPresentation.Utils.ParticipantSeparator;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const { HIDE, SHOW } = SelectionButtonTypes.NAME_DISPLAY;

const MARKET_NAME_DISPLAY = {
    IN_BUTTON: 'inButton',
    OWN_ROW: 'ownRow',
    HIDE: 'hide',
};

const CONTENT_BACKGROUND_TARGET = {
    CARD: 'card',
    JERSEY: 'jersey',
};

export default function PropCard(props) {
    const {
        mojitoTools: { config, stringResolver, appContext },
        card,
        shouldRequestData = true,
    } = props;
    const { event, market, selection, containerConfig, jerseySectionConfig, jerseyImageConfig } =
        usePropCardContent(card.content, config, shouldRequestData);
    const { imageUrl } = card.content;
    const onEventClick = useCallback(() => {
        IntentActionTrigger.showSportEvent(event.sportId, event.id);
        appContext.analyticsEmitter.emitAnalytics('eventCardClicked', event.canonicalName);
    }, [event?.sportId, event?.id, event?.canonicalName, appContext.analyticsEmitter]);

    if (!selection || !event || !market) {
        return null;
    }

    const showMarketInButton = config.marketNameDisplay === MARKET_NAME_DISPLAY.IN_BUTTON;
    const { name, handicapLabel } = MarketHelper.handleHandicap(
        selection.name,
        selection.handicapLabel
    );
    const selectionLabel = [name, handicapLabel].filter(Boolean).join(' ');

    return (
        <FlexPane class={'ta-PropCard'} config={containerConfig}>
            {config.showBadgeRow && (
                <FlexPane class="ta-badgeRow" config={config.badgeRow}>
                    <EventCardBadge config={config.categoryBadge} />
                </FlexPane>
            )}
            <Button
                class="ta-clickableArea"
                onClick={onEventClick}
                hrefLink={NavigationPathBuilder.sportEventPath(
                    event.sportId,
                    event.id,
                    appContext.routeResolver
                )}
                config={config.clickableArea}
            >
                <FlexPane config={config.headerSection} class="ta-header">
                    <Text config={config.playerNameLabel} class="ta-playerName">
                        {selectionLabel}
                    </Text>
                </FlexPane>
                {config.marketNameDisplay === MARKET_NAME_DISPLAY.OWN_ROW && (
                    <FlexPane config={config.marketSection} class="ta-market">
                        <Text config={config.marketNameLabel}>{market.name}</Text>
                    </FlexPane>
                )}
                <FlexPane config={jerseySectionConfig} class="ta-jersey">
                    {imageUrl && (
                        <Image class="ta-jerseyImage" config={jerseyImageConfig} src={imageUrl} />
                    )}
                </FlexPane>
                <MatchInfo config={config} stringResolver={stringResolver} eventItem={event} />
            </Button>
            <FlexPane config={config.footerSection} class="ta-footer">
                <SelectionButton
                    config={config.selectionButton}
                    selection={showMarketInButton ? { ...selection, name: market.name } : selection}
                    event={event}
                    marketStatus={market.status}
                    marketType={market.type}
                    marketId={market.id}
                    nameDisplay={showMarketInButton ? SHOW : HIDE}
                />
            </FlexPane>
        </FlexPane>
    );
}

const MatchInfo = props => {
    const { config, stringResolver, eventItem } = props;
    const { participants, name, homeAway, sportId } = eventItem;
    const hasTwoParticipants = participants?.length === 2;

    return (
        <FlexPane config={config.matchSection} class="ta-match">
            {hasTwoParticipants && (
                <ParticipantIconsInline
                    participants={participants}
                    sportId={sportId}
                    homeAway={homeAway}
                    separator={stringResolver.resolveString(getSeparatorTranslationKey(homeAway))}
                    config={config.participantsInline}
                />
            )}
            {(!hasTwoParticipants || config.showEventName) && (
                <Text config={config.eventNameLabel} class="ta-eventName">
                    {name}
                </Text>
            )}
        </FlexPane>
    );
};

const usePropCardContent = (content, config, shouldRequestData) => {
    const { eventId, marketId, selectionId, backgroundColor, backgroundImage, imageFilter } =
        content;
    const marketIds = useMemo(() => [marketId], [marketId]);
    const event = useEventItem(eventId, shouldRequestData);
    const market = useMarketItems(marketIds, shouldRequestData)?.[0];
    const selection = EventUtils.getSelectionFromMarket(market, selectionId);

    const themesWholeCard = config.contentBackgroundTarget === CONTENT_BACKGROUND_TARGET.CARD;
    const contentBackground = useMemo(
        () => ({
            backgroundColor: backgroundColor || undefined,
            backgroundImage: backgroundImage || undefined,
        }),
        [backgroundColor, backgroundImage]
    );

    const containerConfig = useMemo(
        () =>
            themesWholeCard
                ? uiStyle.merge(config.container, { style: contentBackground })
                : config.container,
        [themesWholeCard, config.container, contentBackground]
    );

    const jerseySectionConfig = useMemo(
        () =>
            themesWholeCard
                ? config.jerseySection
                : uiStyle.merge(config.jerseySection, { style: contentBackground }),
        [themesWholeCard, config.jerseySection, contentBackground]
    );

    const jerseyImageConfig = useMemo(
        () => uiStyle.merge(config.jerseyImage, { style: { filter: imageFilter || undefined } }),
        [imageFilter, config.jerseyImage]
    );

    return { event, market, selection, containerConfig, jerseySectionConfig, jerseyImageConfig };
};
